import React, { useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MenuItems } from '../MenuItems';

interface UseUmbracoDashboardMenuOptions {
  containerId: string;
  dashboardTitles: (string | undefined)[];
  selectedDashboardIndex: number;
  setSelectedDashboardIndex: React.Dispatch<React.SetStateAction<number>>;
  hostElement: React.RefObject<HTMLElement | null>;
}

const backofficeMenuSelector = '#leftcolumn #navigation ul.umb-tree';

export function useUmbracoDashboardMenu({
  containerId,
  dashboardTitles,
  selectedDashboardIndex,
  setSelectedDashboardIndex,
  hostElement,
}: UseUmbracoDashboardMenuOptions) {
  useEffect(() => {
    document.body.classList.remove('emptySection');
  }, []);

  useEffect(() => {
    let menuRoot: Root | null = null;
    let menuList: HTMLElement | null = null;
    let disposed = false;
    let observer: MutationObserver | null = null;

    function removeMenu() {
      menuRoot?.unmount();
      menuRoot = null;
      menuList?.remove();
      menuList = null;
    }

    function dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      removeMenu();
      observer?.disconnect();
      window.removeEventListener('hashchange', dispose);
      window.removeEventListener('popstate', dispose);
    }

    function removeStaleMenu(menu: Element) {
      menu.querySelector(`#${containerId}`)?.remove();
    }

    function renderMenu() {
      const menu = document.querySelector(backofficeMenuSelector);

      if (!menu || disposed) {
        return;
      }

      removeMenu();
      removeStaleMenu(menu);

      menuList = document.createElement('ul');
      menuList.id = containerId;
      menuList.dataset.agoraBackofficeDashboardMenu = 'true';
      menu.appendChild(menuList);

      menuRoot = createRoot(menuList);
      menuRoot.render(
        <MenuItems
          dashboardTitles={dashboardTitles}
          selectedDashboardIndex={selectedDashboardIndex}
          setSelectedDashboardIndex={setSelectedDashboardIndex}
        />,
      );
    }

    renderMenu();

    observer = new MutationObserver(() => {
      const host = hostElement.current;

      if (host && !document.body.contains(host)) {
        dispose();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('hashchange', dispose);
    window.addEventListener('popstate', dispose);

    return dispose;
  }, [
    containerId,
    dashboardTitles,
    selectedDashboardIndex,
    setSelectedDashboardIndex,
    hostElement,
  ]);
}
